import React, { useEffect, useMemo, useState } from 'react';
import { onValue, ref } from 'firebase/database';
import { FiAlertTriangle, FiCheckCircle, FiClock, FiFileText, FiSearch } from 'react-icons/fi';

import { db } from '../../firebase';
import DeviceHistory from './DeviceHistory';

// LogsTab — health history (MDM analytics, DeviceHistory) on top, then the
// chronological feed of commands the device has executed.
//
// Subscribes to mdm/device_logs/<MAC> for the lifetime of the tab. Each
// entry is a small object written by the dashboard at command time:
//   { command, params?, action?, timestamp, status, error? }
// Sorted newest-first; status is colour-coded; failures expose `error`.

export default function LogsTab({ mac }) {
    const [logs, setLogs] = useState({});
    const [query, setQuery] = useState('');

    useEffect(() => {
        if (!mac) return undefined;
        const logsRef = ref(db, `mdm/device_logs/${mac}`);
        const unsub = onValue(logsRef, (snap) => setLogs(snap.val() || {}));
        return () => unsub();
    }, [mac]);

    const sorted = useMemo(() => {
        const list = Object.entries(logs).map(([id, entry]) => ({ id, ...entry }));
        list.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
        const q = query.trim().toLowerCase();
        if (!q) return list;
        return list.filter(
            (e) =>
                (e.command && String(e.command).toLowerCase().includes(q)) ||
                (e.action && String(e.action).toLowerCase().includes(q)) ||
                (e.error && String(e.error).toLowerCase().includes(q))
        );
    }, [logs, query]);

    if (Object.keys(logs).length === 0) {
        return (
            <>
                <DeviceHistory mac={mac} />
                <div className="rounded-xl border border-dashed border-white/[0.08] p-10 text-center text-gray-500">
                    <FiFileText className="mx-auto text-3xl mb-3 text-gray-500" />
                    No commands have been logged for this device yet.
                </div>
            </>
        );
    }

    return (
        <>
        <DeviceHistory mac={mac} />
        <section className="card-chrome overflow-hidden">
            <header className="px-5 py-3 border-b border-white/[0.06] flex items-center justify-between gap-3 flex-wrap">
                <h2 className="text-base font-semibold text-brand-400 inline-flex items-center gap-2">
                    <FiFileText /> Command Log
                    <span className="text-xs text-gray-500 font-normal">({sorted.length})</span>
                </h2>
                <label className="relative">
                    <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 text-sm" />
                    <input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Filter…"
                        className="bg-surface-inset border border-white/[0.06] rounded-md pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-brand/40 min-w-[220px]"
                    />
                </label>
            </header>

            <ul className="divide-y divide-white/[0.06]">
                {sorted.map((entry) => (
                    <LogRow key={entry.id} entry={entry} />
                ))}
            </ul>
        </section>
        </>
    );
}

function LogRow({ entry }) {
    const isSuccess = entry.status === 'success';
    const isFailed = entry.status === 'failed' || Boolean(entry.error);

    return (
        <li className="px-5 py-3">
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                    {isFailed ? (
                        <FiAlertTriangle className="text-red-400 shrink-0" />
                    ) : isSuccess ? (
                        <FiCheckCircle className="text-green-400 shrink-0" />
                    ) : (
                        <FiClock className="text-yellow-400 shrink-0" />
                    )}
                    <span className="font-medium text-gray-100 truncate">
                        {entry.command || entry.action || 'unknown'}
                    </span>
                    {entry.status && !isFailed && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                            isSuccess ? 'bg-green-500/15 text-green-400' : 'bg-yellow-500/15 text-yellow-400'
                        }`}>
                            {entry.status}
                        </span>
                    )}
                </div>
                <span className="text-xs text-gray-500 whitespace-nowrap">
                    {entry.timestamp ? new Date(entry.timestamp).toLocaleString() : '—'}
                </span>
            </div>

            {entry.error && (
                <p className="mt-1 text-xs text-red-300 break-all">{entry.error}</p>
            )}
            {entry.params && Object.keys(entry.params).length > 0 && (
                <p className="mt-1 text-xs text-gray-500 font-mono break-all">
                    params: {JSON.stringify(entry.params)}
                </p>
            )}
        </li>
    );
}
